import React from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt, Percent, Wallet } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { primaryText, semiBoldText, iconBgBlue, iconBgAmber, iconBgGreen } from '../../../styling/stylingHelper';
import { formatDateLong } from '../../../services/helperService';
import { Platform } from '../../../types/Platforms';
import { StatCard } from '../../ui/StatCard';
import { SupplierLogo } from '../../ui/SupplierLogo';
import { formatQuantityInfo } from '../../../utils/quantityUtils';
import { QuantityInfo } from '../../../stores/appOrderHistoryStore';
import { getOrderVatAmount, getOrderVatRate } from '../../orderhistory/vatUtils';

interface ProductData {
  articleId: string;
  name: string;
  quantity: number;
  price: number;
  rawQuantity: number;
  quantityInfo?: QuantityInfo;
  priceWithTaxes?: number;
  taxRate?: number;
  category?: string;
}

interface OrderData {
  platform: Platform;
  clientName: string;
  orderNumber: string;
  orderDate: string;
  total: number;
  totalWithTaxes?: number;
  discount?: number;
  shipping?: number;
  productData: ProductData[];
  taxCalculationMethod?: string;
  unknownSupplierName?: string;
}

interface InvoiceDataComponentProps {
  orderData: OrderData[];
  platform: Platform;
}

const ProductItem = ({ product }: { product: ProductData }) => {
  const { t } = useTranslation();
  const productTotal = product.quantity * product.price;
  const quantityForDisplay = product.rawQuantity || product.quantity;
  const quantityDisplay = product.quantityInfo ? formatQuantityInfo(quantityForDisplay, product.quantityInfo, product.category) : null;

  return (
    <div className="flex items-center justify-between p-3 bg-background dark:bg-background-dark hover:bg-muted/10 dark:hover:bg-muted-dark/10 rounded-lg border border-border dark:border-border-dark transition-colors">
      <div className="flex-1">
        {quantityDisplay && (
          <div className="mb-2">
            <span className="inline-block px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-md">
              {quantityDisplay.displayText}
            </span>
          </div>
        )}
        <div className="font-semibold text-text dark:text-text-dark text-sm">
          {product.name || t('invoices.unknownProduct')}
        </div>
        {product.articleId && product.articleId !== 'no_article_id' && (
          <div className="text-xs text-muted dark:text-muted-dark font-mono mt-1 flex items-center gap-3">
            <span>{t('invoices.productIdShort')}: {product.articleId}</span>
            {product.rawQuantity !== undefined && (
              <span>{t('invoices.quantityShort')}: {product.rawQuantity}</span>
            )}
          </div>
        )}
      </div>
      <div className="text-right ml-4">
        <div className="text-base font-bold text-text dark:text-text-dark">
          €{productTotal.toFixed(2)}
        </div>
        <div className="text-xs text-muted dark:text-muted-dark">
          {product.quantity.toFixed(1)} × €{(product.price || 0).toFixed(2)}
        </div>
      </div>
    </div>
  );
};

// Calculate VAT rate if possible (returns number or undefined)
function getCalculatedTaxRate(order: OrderData): number | undefined {
  if (
    typeof order.total === 'number' &&
    typeof order.totalWithTaxes === 'number' &&
    order.total > 0
  ) {
    return getOrderVatRate(order.platform, order.totalWithTaxes ?? 0, order.total ?? 0, order.shipping ?? 0, order.productData);
  }
  return undefined;
}

const OrderSection = ({ order, platform }: { order: OrderData; platform: Platform }) => {
  const { t } = useTranslation();
  const formattedDate = order.orderDate ? formatDateLong(new Date(order.orderDate)) : t('invoices.unknownDate');

  // Sort products by total spend
  const sortedProducts = [...(order.productData || [])].sort((a, b) => {
    const totalA = (a.quantity || 0) * (a.price || 0);
    const totalB = (b.quantity || 0) * (b.price || 0);
    return totalB - totalA;
  });

  // Calculate total sum of products
  const productsTotalSum = sortedProducts.reduce(
    (sum, product) => sum + (product.quantity || 0) * (product.price || 0),
    0
  );

  // Try to calculate VAT rate if not present
  const calculatedTaxRate = getCalculatedTaxRate(order);

  return (
    <div className="space-y-4">
      {/* Order Header */}
      <div className="bg-backgroundLight dark:bg-backgroundLight-dark rounded-lg p-4">
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {platform !== Platform.ANY_INVOICE && (
            <>
              <SupplierLogo
                platform={platform}
                unknownSupplierName={order.unknownSupplierName}
                size="large"
                className="inline-block"
                alt={platform}
              />
              <span className="text-xl font-bold text-text dark:text-text-dark">•</span>
            </>
          )}
          {platform === Platform.ANY_INVOICE && order.unknownSupplierName && (
            <>
              <span className="text-xl font-bold text-accent dark:text-accent-dark">{order.unknownSupplierName}</span>
              <span className="text-xl font-bold text-text dark:text-text-dark">•</span>
            </>
          )}
          <span className="text-xl font-bold text-text dark:text-text-dark">{formattedDate}</span>
          <span className="text-xl font-bold text-text dark:text-text-dark">•</span>
          <span className="text-xl font-bold text-text dark:text-text-dark">{order.clientName || t('invoices.unknownClient')}</span>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard
          icon={<Receipt className="w-6 h-6" style={{ color: '#2E90FA' }} />}
          iconBg={iconBgBlue}
          title={t('orderHistory.totalExclVat')}
          value={order.total}
          valueType="currency"
        />

        {order.totalWithTaxes !== undefined && (
          <StatCard
            icon={<Percent className="w-6 h-6" style={{ color: '#F59E0B' }} />}
            iconBg={iconBgAmber}
            title={t('orderHistory.vat')}
            value={getOrderVatAmount(order.platform, order.totalWithTaxes ?? 0, order.total ?? 0, order.shipping ?? 0)}
            valueType="currency"
          />
        )}

        {order.totalWithTaxes !== undefined && (
          <StatCard
            icon={<Wallet className="w-6 h-6" style={{ color: '#079455' }} />}
            iconBg={iconBgGreen}
            title={t('orderHistory.totalInclVat')}
            value={order.totalWithTaxes}
            valueType="currency"
          />
        )}
      </div>

      {/* Order Metadata */}
      <div className="bg-backgroundLight dark:bg-backgroundLight-dark rounded-lg p-4">
        <h3 className={cn(primaryText, semiBoldText, "text-sm mb-3")}>
          {t('invoices.orderInformation')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {platform === Platform.ANY_INVOICE && order.unknownSupplierName && (
            <div className="flex gap-2">
              <span className="font-medium text-muted dark:text-muted-dark min-w-[100px]">
                {t('orderHistory.supplierTableHeader')}:
              </span>
              <span className="text-text dark:text-text-dark font-semibold">
                {order.unknownSupplierName}
              </span>
            </div>
          )}
          <div className="flex gap-2">
            <span className="font-medium text-muted dark:text-muted-dark min-w-[100px]">
              {t('orderHistory.orderNumberTableHeader')}:
            </span>
            <span className="text-text dark:text-text-dark font-mono font-semibold">
              {order.orderNumber}
            </span>
          </div>
          <div className="flex gap-2">
            <span className="font-medium text-muted dark:text-muted-dark min-w-[100px]">
              {t('invoices.platformLabel')}:
            </span>
            <span className="text-text dark:text-text-dark capitalize font-semibold">
              {platform}
            </span>
          </div>
          {calculatedTaxRate !== undefined && (
            <div className="flex gap-2">
              <span className="font-medium text-muted dark:text-muted-dark min-w-[100px]">
                {t('orderHistory.vatRate')}:
              </span>
              <span className="text-text dark:text-text-dark font-semibold">
                {(calculatedTaxRate * 100).toFixed(0)}%
              </span>
            </div>
          )}
          {order.discount !== undefined && order.discount > 0 && (
            <div className="flex gap-2">
              <span className="font-medium text-muted dark:text-muted-dark min-w-[100px]">
                {t('orderHistory.discount')}:
              </span>
              <span className="text-text dark:text-text-dark font-semibold">
                -€{order.discount.toFixed(2)}
              </span>
            </div>
          )}
          {order.shipping !== undefined && order.shipping > 0 && (
            <div className="flex gap-2">
              <span className="font-medium text-muted dark:text-muted-dark min-w-[100px]">
                {t('orderHistory.shipping')}:
              </span>
              <span className="text-text dark:text-text-dark font-semibold">
                €{order.shipping.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Products */}
      <div className="bg-backgroundLight dark:bg-backgroundLight-dark rounded-lg p-4">
        <h3 className={cn(primaryText, semiBoldText, "text-sm mb-3")}>
          {t('invoices.productsWithCount', { count: sortedProducts.length })}
          <span className="ml-2 text-muted dark:text-muted-dark">
            | {t('invoices.productsSum', { amount: `€${productsTotalSum.toFixed(2)}` })}
          </span>
        </h3>
        <div className="space-y-2">
          {sortedProducts.length > 0 ? (
            sortedProducts.map((product, index) => (
              <ProductItem
                key={product.articleId || `product-${index}`}
                product={product}
              />
            ))
          ) : (
            <div className="py-4 text-center text-muted dark:text-muted-dark text-sm">
              {t('invoices.noProductsFound')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const InvoiceDataComponent: React.FC<InvoiceDataComponentProps> = ({ orderData, platform }) => {
  const { t } = useTranslation();
  const platformEnum = platform as Platform;

  if (!orderData || orderData.length === 0) {
    return (
      <div className="text-center py-8 text-muted dark:text-muted-dark">
        {t('invoices.noOrderData')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {orderData.map((order, index) => (
        <div key={`${order.orderNumber}-${index}`}>
          {orderData.length > 1 && (
            <h2 className={cn(primaryText, semiBoldText, "text-base mb-3")}>
              {t('invoices.orderXofY', { index: index + 1, total: orderData.length })}
            </h2>
          )}
          <OrderSection order={order} platform={platformEnum} />
        </div>
      ))}
    </div>
  );
};
